import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, X, UserPlus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  inactive: "bg-slate-100 text-slate-500 dark:bg-ink-panel dark:text-slate-400",
};

export default function Users() {
  const { hasPermission, user: me } = useAuth();
  const canCreate = hasPermission("users.create");
  const canEdit = hasPermission("users.edit");
  const canDelete = hasPermission("users.delete");

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode, user}

  const load = async () => {
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
      setUsers(u.data);
      setRoles(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600 dark:text-ink-gold" /></div>;

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Access Management</p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Users Management</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage workspace members and their role assignments.</p>
        </div>
        {canCreate && (
          <button onClick={() => setModal({ mode: "create", user: { email: "", name: "", password: "", role_keys: [], status: "active" } })} data-testid="add-user-button" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 dark:bg-ink-gold dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep">
            <UserPlus className="h-4 w-4" /> Add user
          </button>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-ink-line dark:bg-ink-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 dark:border-ink-line dark:bg-ink/40">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Roles</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 dark:border-ink-line dark:hover:bg-ink-panel/40" data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-800 dark:text-ink-text">{u.name}</td>
                  <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{u.email}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {u.role_keys.map((r) => (
                        <span key={r} className="rounded bg-sky-50 px-1.5 py-0.5 text-[11px] font-medium text-sky-700 dark:bg-ink-gold/10 dark:text-ink-gold">{r}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[u.status] || STATUS_STYLES.inactive}`}>{u.status}</span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-1">
                      {canEdit && (
                        <button onClick={() => setModal({ mode: "edit", user: { ...u, password: "" } })} data-testid={`edit-user-${u.id}`} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-sky-600 dark:hover:bg-ink-panel">
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {canDelete && u.id !== me?.id && (
                        <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`} className="rounded-md p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <UserModal
          modal={modal}
          roles={roles}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function UserModal({ modal, roles, onClose, onSaved }) {
  const isCreate = modal.mode === "create";
  const [form, setForm] = useState(modal.user);
  const [saving, setSaving] = useState(false);

  const toggleRole = (key) =>
    setForm((f) => ({
      ...f,
      role_keys: f.role_keys.includes(key) ? f.role_keys.filter((k) => k !== key) : [...f.role_keys, key],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isCreate) {
        await api.post("/users", { email: form.email, name: form.name, password: form.password, role_keys: form.role_keys, status: form.status });
        toast.success("User created");
      } else {
        const payload = { name: form.name, role_keys: form.role_keys, status: form.status };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${form.id}`, payload);
        toast.success("User updated");
      }
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="user-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-ink-line">
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-ink-text">{isCreate ? "Add User" : "Edit User"}</h3>
          <button onClick={onClose} data-testid="user-modal-close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="max-h-[70vh] space-y-4 overflow-y-auto px-6 py-5">
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Full Name</label>
            <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="user-name-input" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Email</label>
            <input required type="email" disabled={!isCreate} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="user-email-input" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500 dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">{isCreate ? "Password" : "New Password (optional)"}</label>
            <input type="password" required={isCreate} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="user-password-input" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Roles</label>
            <div className="grid grid-cols-2 gap-2" data-testid="user-roles-picker">
              {roles.map((r) => (
                <label key={r.key} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${form.role_keys.includes(r.key) ? "border-sky-300 bg-sky-50 dark:border-ink-gold/40 dark:bg-ink-gold/10" : "border-slate-200 dark:border-ink-line"}`}>
                  <input type="checkbox" checked={form.role_keys.includes(r.key)} onChange={() => toggleRole(r.key)} data-testid={`user-role-${r.key}`} className="accent-sky-600 dark:accent-ink-gold" />
                  <span className="text-slate-700 dark:text-slate-200">{r.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Status</label>
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} data-testid="user-status-select" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text">
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300">Cancel</button>
            <button type="submit" disabled={saving} data-testid="user-modal-save" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 dark:bg-ink-gold dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {isCreate ? "Create" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
